import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Home, Package, Utensils, Users, PlusSquare, LayoutGrid, Drumstick, Tag, Calendar, Box, Folder, ClipboardList, LogOut } from "lucide-react"; // Importing appropriate icons
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAdmin, logoutAdmin } from '../redux/adminSlice';
import ProductCrudPage from './ProductCrudPage';  // Importing the page
import UserCrudPage from './UsersCrudPage';
import MealPage from './MealPage';
import MealPackagePage from './MealPackage';
import MealTypePage from './MealTypePage';
import MealPlanPage from './MealPlanPage';
import AddOnPage from './AddOnPage';
import OrderDetailsPage from './OrderDetailsPage';
import CategoryPage from './CategoryPage';
import ImageCustomizer from './ImageCustomizer';

const Sidebar = ({ onSelect, selectedMenu, onLogout }) => {
    return (
        <div className="w-64 h-auto min-h-screen bg-slate-900 text-slate-300 flex flex-col justify-between shadow-xl">
            <div>
                <div className="p-6 border-b border-slate-700">
                    <div className="text-2xl font-bold text-white tracking-wide">Daily Fit</div>
                    <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">Admin Panel</div>
                </div>
                <ul className="py-6 space-y-1">
                    {[
                        { id: 'category', label: 'Category', icon: Folder },
                        { id: 'meal', label: 'Meal', icon: Drumstick },
                        { id: 'mealType', label: 'Meal Type', icon: Tag },
                        { id: 'mealPlan', label: 'Meal Plan', icon: Calendar },
                        { id: 'mealPackage', label: 'Meal Package', icon: Box },
                        { id: 'addProduct', label: 'Add On Product', icon: PlusSquare },
                        { id: 'users', label: 'Users Overview', icon: Users },
                        { id: 'orders', label: 'Orders Overview', icon: ClipboardList }
                    ].map((item) => (
                        <li
                            key={item.id}
                            className={`px-6 py-3 cursor-pointer flex items-center transition-all duration-200 border-l-4 ${selectedMenu === item.id
                                ? 'bg-slate-800 text-white border-blue-500'
                                : 'border-transparent hover:bg-slate-800 hover:text-white'
                                }`}
                            onClick={() => onSelect(item.id)}
                        >
                            <item.icon className={`mr-3 h-5 w-5 ${selectedMenu === item.id ? 'text-blue-400' : 'text-slate-400'}`} />
                            <span className="font-medium">{item.label}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="p-4 border-t border-slate-800 space-y-1">
                <div
                    className="flex items-center px-4 py-3 cursor-pointer text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    onClick={() => onSelect('settings')}
                >
                    <Settings className="mr-3 h-5 w-5" />
                    <span className="font-medium">Settings</span>
                </div>
                <div
                    className="flex items-center px-4 py-3 cursor-pointer text-red-400 hover:text-white hover:bg-red-950/30 rounded-lg transition-colors"
                    onClick={onLogout}
                >
                    <LogOut className="mr-3 h-5 w-5 text-red-400" />
                    <span className="font-medium">Logout</span>
                </div>
            </div>
        </div>
    );
};

const AdminDashboardPage = () => {
    const [selectedMenu, setSelectedMenu] = useState('meal');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const adminInfo = useSelector(selectAdmin);

    const handleLogout = useCallback(() => {
        dispatch(logoutAdmin());
        sessionStorage.removeItem("token");
        navigate('/admin-login');
    }, [dispatch, navigate]);

    useEffect(() => {
        if (!adminInfo) {
            navigate('/admin-login');
            return;
        }

        let timeoutId;
        const INACTIVITY_TIME = 2 * 60 * 1000; // 2 minutes in milliseconds

        const resetTimer = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(handleAutoLogout, INACTIVITY_TIME);
        };

        const handleAutoLogout = () => {
            console.log("Admin inactive for 2 minutes. Logging out...");
            handleLogout();
        };

        // Event listeners to track activity
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click'
        ];

        // Initialize timer
        resetTimer();

        // Add event listeners
        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        // Cleanup
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [adminInfo, navigate, handleLogout]);

    // Prevent rendering dashboard elements if not authenticated to stop direct access
    if (!adminInfo) {
        return null;
    }

    const renderContent = () => {
        switch (selectedMenu) {
            case 'mealPlan':
                return <MealPlanPage />;
            case 'users':
                return <UserCrudPage />;
            case 'addProduct':
                return <AddOnPage />;
            case 'layoutDesign':
                return <ImageCustomizer />;
            case 'category':
                return <CategoryPage />;
            case 'meal':
                return <MealPage />;
            case 'mealType':
                return <MealTypePage />;
            case 'mealPackage':
                return <MealPackagePage />;
            case 'orders':
                return <OrderDetailsPage />;
            default:
                return <ProductCrudPage />;
        }
    };


    const getMenuTitle = () => {
        switch (selectedMenu) {
            case 'users':
                return 'Users List';
            case 'mealPlan':
                return 'Meal Plan';
            case 'addProduct':
                return 'Add On Product';
            case 'layoutDesign':
                return 'Image Customizer';
            case 'category':
                return 'Category List';
            case 'meal':
                return 'Meal';
            case 'mealType':
                return 'Meal Type';
            case 'mealPackage':
                return 'Meal Package';
            case 'orders':
                return 'Ordes List';
            default:
                return 'Daily Fit';
        }
    };


    return (
        <div className="flex h-screen overflow-hidden bg-gray-100">
            <Sidebar onSelect={setSelectedMenu} selectedMenu={selectedMenu} onLogout={handleLogout} />
            <div className="flex-1 bg-gray-100 h-screen overflow-y-auto">
                <div className="flex justify-between items-center bg-white p-4 shadow sticky top-0 z-20">
                    <div className="text-xl font-bold">{getMenuTitle()}</div>
                    <Home className="cursor-pointer" onClick={() => setSelectedMenu('meal')} />
                </div>
                <div className="p-4">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboardPage;
